<script lang="ts">
  import { user } from '../lib/stores';
  import { hasNostr, login, loginWithGeneratedKey, loginWithNsec, loginWithMnemonic } from '../lib/auth';

let loading = $state(false);
  let error = $state('');
  let errorTimer = $state<ReturnType<typeof setTimeout> | null>(null);
  let extensionAvailable = $state(hasNostr());
  let showNsecInput = $state(false);
  let nsecInput = $state('');
  let showAdvanced = $state(false);
  let mnemonicInput = $state('');
  let keyRevealOpen = $state(false);
  let generatedNsec = $state('');
  let pendingPubkey = $state<string | null>(null);
  let confirmHint = $state('Copy or download your key, then confirm below.');

  $effect(() => {
    if (extensionAvailable) return;
    const checks = [100, 300, 1000, 2000];
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const delay of checks) {
      timers.push(setTimeout(() => {
        if (hasNostr()) extensionAvailable = true;
      }, delay));
    }
    return () => timers.forEach(clearTimeout);
  });

  function showError(msg: string) {
    error = msg;
    if (errorTimer) clearTimeout(errorTimer);
    errorTimer = setTimeout(() => { error = ''; }, 5000);
  }

  async function handleExtensionLogin() {
    if (loading) return;
    loading = true;
    error = '';
    try {
      const pubkey = await login();
      user.set(pubkey);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Login failed');
    } finally {
      loading = false;
    }
  }

  async function handleGenerate() {
    if (loading) return;
    loading = true;
    error = '';
    try {
      const { pubkey, nsec, isNew } = await loginWithGeneratedKey();
      if (isNew) {
        generatedNsec = nsec;
        confirmHint = 'Copy or download your key, then confirm below.';
        keyRevealOpen = true;
        pendingPubkey = pubkey;
      } else {
        user.set(pubkey);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Key generation failed');
    } finally {
      loading = false;
    }
  }

  async function handleNsecLogin() {
    const nsec = nsecInput.trim();
    if (!nsec.startsWith('nsec1')) {
      showError('Invalid nsec — must start with nsec1');
      return;
    }
    if (loading) return;
    loading = true;
    error = '';
    try {
      const pubkey = await loginWithNsec(nsec);
      user.set(pubkey);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to decode nsec');
    } finally {
      loading = false;
    }
  }

  async function handleMnemonicLogin() {
    const mnemonic = mnemonicInput.trim();
    if (mnemonic.split(/\s+/).length < 12) {
      showError('Seed phrase must be at least 12 words');
      return;
    }
    if (loading) return;
    loading = true;
    error = '';
    try {
      const pubkey = await loginWithMnemonic(mnemonic);
      user.set(pubkey);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to derive key from seed phrase');
    } finally {
      loading = false;
    }
  }

  function copyNsec() {
    navigator.clipboard.writeText(generatedNsec).then(() => {
      confirmHint = 'Key copied! Paste it somewhere safe before continuing.';
    });
  }

  function downloadNsec() {
    const blob = new Blob([generatedNsec + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blossomflare-nsec.txt';
    a.click();
    URL.revokeObjectURL(url);
    confirmHint = 'File downloaded! Store it somewhere secure.';
  }

  function closeKeyReveal() {
    keyRevealOpen = false;
    if (pendingPubkey) {
      user.set(pendingPubkey);
      pendingPubkey = null;
    }
  }
</script>

<svelte:window onkeydown={(e) => keyRevealOpen && e.key === 'Escape' && closeKeyReveal()} />

<div class="flex flex-col gap-4 w-full max-w-sm">
  {#if extensionAvailable}
    <button
      onclick={handleExtensionLogin}
      disabled={loading}
      class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#238636] hover:bg-[#2ea043] disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors w-full"
    >
      {#if loading}
        <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span>Connecting...</span>
      {:else}
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
        <span>Sign In with Extension</span>
      {/if}
    </button>
  {/if}

  <button
    onclick={handleGenerate}
    disabled={loading}
    class="inline-flex items-center justify-center gap-2 px-5 py-2.5 {extensionAvailable ? 'bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] border border-[#30363d]' : 'bg-[#238636] hover:bg-[#2ea043] text-white'} disabled:opacity-50 rounded-md text-sm font-medium transition-colors w-full"
  >
    {#if loading}
      <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span>Generating...</span>
    {:else}
      <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 5v14M5 12h14" />
      </svg>
      <span>Generate New Key</span>
    {/if}
  </button>

  <div class="flex items-center gap-2 text-xs text-[#484f58]">
    <div class="flex-1 h-px bg-[#21262d]"></div>
    <span>or paste nsec</span>
    <div class="flex-1 h-px bg-[#21262d]"></div>
  </div>

  {#if showNsecInput}
    <div class="space-y-2">
      <div class="text-xs text-[#f85149] bg-[#f8514910] border border-[#f8514933] rounded-md p-2 leading-relaxed">
        <strong>Risky.</strong> Anyone with this key has full access. Consider using an extension instead.
      </div>
      <textarea
        bind:value={nsecInput}
        placeholder="nsec1..."
        rows="2"
        class="w-full text-xs font-mono bg-[#0d1117] border border-[#21262d] rounded-md p-2 text-[#e6edf3] focus:border-[#58a6ff] focus:outline-none resize-none"
      ></textarea>
      <button
        onclick={handleNsecLogin}
        disabled={loading || !nsecInput.trim()}
        class="w-full px-4 py-2 bg-[#21262d] hover:bg-[#30363d] disabled:opacity-50 text-[#e6edf3] rounded-md text-sm font-medium transition-colors"
      >
        Sign In with nsec
      </button>
    </div>
  {:else}
    <button
      onclick={() => showNsecInput = true}
      class="text-xs text-[#58a6ff] hover:underline text-center"
    >
      Sign in with nsec
    </button>
  {/if}

  <button
    onclick={() => showAdvanced = !showAdvanced}
    class="text-xs text-[#484f58] hover:text-[#8b949e] text-center transition-colors"
  >
    {showAdvanced ? '▲' : '▼'} Advanced
  </button>

  {#if showAdvanced}
    <div class="space-y-2">
      <div class="text-xs text-[#f85149] bg-[#f8514910] border border-[#f8514933] rounded-md p-2 leading-relaxed">
        <strong>Dangerous.</strong> A seed phrase compromises <em>every</em> key derived from it — not just this one.
        Use an extension or generate a new key instead if possible.
      </div>
      <textarea
        bind:value={mnemonicInput}
        placeholder="abandon ability able about above absent absorb abstract absurd abuse access accident..."
        rows="3"
        class="w-full text-xs font-mono bg-[#0d1117] border border-[#21262d] rounded-md p-2 text-[#e6edf3] focus:border-[#58a6ff] focus:outline-none resize-none"
      ></textarea>
      <button
        onclick={handleMnemonicLogin}
        disabled={loading || !mnemonicInput.trim()}
        class="w-full px-4 py-2 bg-[#21262d] hover:bg-[#30363d] disabled:opacity-50 text-[#e6edf3] rounded-md text-sm font-medium transition-colors"
      >
        Derive Key from Seed Phrase
      </button>
    </div>
  {/if}

  {#if !extensionAvailable}
    <div class="text-xs text-[#484f58] text-center leading-relaxed">
      No extension detected.
      <a href="https://getalby.com" target="_blank" rel="noopener noreferrer" class="text-[#58a6ff] hover:underline">Alby</a>
      ·
      <a href="https://github.com/fiatjaf/nos2x" target="_blank" rel="noopener noreferrer" class="text-[#58a6ff] hover:underline">nos2x</a>
      ·
      <a href="https://nostore.app" target="_blank" rel="noopener noreferrer" class="text-[#58a6ff] hover:underline">Nostore</a>
    </div>
  {/if}

  {#if error}
    <p class="text-xs text-[#f85149] text-center" title={error}>{error}</p>
  {/if}
</div>

{#if keyRevealOpen}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
    <div role="dialog" aria-modal="true" tabindex="-1" class="bg-[#161b22] border border-[#21262d] rounded-lg p-6 max-w-md w-full">
      <h2 class="text-lg font-bold text-[#e6edf3] mb-3">Save Your Identity</h2>
      <div class="bg-[#f8514910] border border-[#f8514933] rounded-md p-3 mb-4">
        <p class="text-xs text-[#f85149] leading-relaxed">
          <strong>This is your only key.</strong> If you lose it, your uploads cannot be recovered.
          There is no password reset, no email recovery, no support team.
        </p>
      </div>
      <div class="bg-[#0d1117] border border-[#21262d] rounded-md p-3 mb-4">
        <code class="text-xs text-[#3fb950] break-all select-all">{generatedNsec}</code>
      </div>
      <div class="flex gap-2 mb-4">
        <button onclick={copyNsec} class="flex-1 px-3 py-2 bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] rounded-md text-xs font-medium transition-colors">
          Copy Key
        </button>
        <button onclick={downloadNsec} class="flex-1 px-3 py-2 bg-[#21262d] hover:bg-[#30363d] text-[#e6edf3] rounded-md text-xs font-medium transition-colors">
          Download
        </button>
      </div>
      <p class="text-xs text-[#8b949e] mb-4">{confirmHint}</p>
      <button onclick={closeKeyReveal} class="w-full px-4 py-2.5 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-sm font-medium transition-colors">
        I've Saved My Key
      </button>
    </div>
  </div>
{/if}
